import React, { useLayoutEffect } from 'react'
import Helmet from 'react-helmet'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { subscriptionPlanSelectors } from 'reduxModules/subscriptions/selectors'
import {
  useFeatureFlagVariant,
  useFeatureFlagsReady
} from '../../utils/useFeatureFlag'

import Collapsible from 'react-collapsible'
import JsonLd from 'components/generic/JsonLd'
import FAQ_ITEMS from './faqItems'

import { subscriptionPlansDispatcher as dispatchSelector } from 'reduxModules/subscriptions/dispatchers'
// import SubscriptionPlanBox from './SubscriptionPlanBox'
import PLANS from './fixturePlans'
import SubscriptionPlanList from './SubscriptionPlanList'
import LifetimePromo from './LifetimePromo'

import './styles.css'
import LoadingComponent from '../generic/LoadingComponent'
import TestimonialVideos from '../generic/TestimonialVideos'
import { GROUP_SUBSCRIPTION_INFO_URL } from '../../utils/externalLinks'

// SPEC-102: the Lifetime A/B test. Read only for the audience (no active
// subscription), since the read is what PostHog counts as exposure; the
// variant goes to the store, where lifetimeEnabledSelector turns 'lifetime'
// into the page as with lifetimeEnabled true (column, promo, Group Learning
// swapped out). Anything else, the flag not loaded, or PostHog blocked
// leaves today's page.
export const LIFETIME_TEST_FLAG = 'plans-lifetime-test'
// How long the page holds the plan table for the flags before showing
// control: long enough for the usual /flags round trip, short enough that a
// blocked PostHog costs a blink, not a wait.
export const LIFETIME_TEST_FLAG_TIMEOUT_MS = 1000

export function SubscriptionPlans({
  isGlobalPromoEnabled,
  globalPromo = {
    codename: '',
    discount: 0,
    discountType: '',
    plans: {}
  },
  usedFreeTrial,
  router,
  isMobile,
  subscriptionPlansLanguage,
  lifetimeEnabled,
  lifetimeTestEligible = false,
  setLifetimeTestVariant = () => {}
}) {
  const {
    headlineTitle,
    subHeadline1,
    subHeadline2,
    discount,
    codename,
    isLoading: promoIsLoading
  } = globalPromo

  const lifetimeTestVariant = useFeatureFlagVariant(LIFETIME_TEST_FLAG, {
    enabled: lifetimeTestEligible
  })
  const flagsReady = useFeatureFlagsReady(LIFETIME_TEST_FLAG_TIMEOUT_MS, {
    enabled: lifetimeTestEligible
  })
  // Before paint, so the store (and lifetimeEnabled) catch up in the same
  // frame the variant lands; cleared when the page goes, so a later visit
  // never starts from a previous visitor's draw.
  useLayoutEffect(() => {
    setLifetimeTestVariant(lifetimeTestVariant)
    return () => setLifetimeTestVariant(null)
  }, [lifetimeTestVariant])
  // No flash of the wrong layout: for the audience the plan table (the only
  // part the variant changes) waits for the flags, and for the 'lifetime'
  // draw until the store reflects it. The hero never waits.
  const plansPending =
    lifetimeTestEligible &&
    (!flagsReady || (lifetimeTestVariant === 'lifetime' && !lifetimeEnabled))
  const isLoading = promoIsLoading

  return (
    <main id="SubscriptionPlans">
      <Helmet>
        <title>
          Subscription Plans | Stan Winston School of Character Arts
        </title>
        <meta
          name="description"
          content="Start a 7-day free trial and stream unlimited special-effects and character-creation courses. Choose a monthly or yearly Stan Winston School subscription."
        />
      </Helmet>
      {isLoading && <LoadingComponent />}
      {!isLoading && (
        <section className="hero">
          <div className="hero-image">
            <div className="overlay" />
            <div className="wrapper">
              <h1 className="headline">
                {isGlobalPromoEnabled && headlineTitle
                  ? headlineTitle
                  : subscriptionPlansLanguage?.headline}
              </h1>
              {!isGlobalPromoEnabled ? (
                <h2>{subscriptionPlansLanguage?.subheadline}</h2>
              ) : (
                <h2>
                  {subHeadline1 || `Save ${discount}% with code ${codename}`}
                </h2>
              )}
              {isGlobalPromoEnabled && (
                <h3>
                  {subHeadline2 ||
                    'Discount lasts as long as you remain a subscriber!'}
                </h3>
              )}
            </div>
          </div>
        </section>
      )}
      {/* <section className="plan-boxes wrapper">
        {plans.map(plan => (
          <SubscriptionPlanBox
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...plan}
            router={router}
            usedFreeTrial={usedFreeTrial}
            key={plan.id}
          />
        ))}
      </section> */}
      {!isLoading && plansPending && <LoadingComponent />}
      {!isLoading && !plansPending && (
        <div className="smaller-wrapper plans">
          <SubscriptionPlanList
            router={router}
            plans={PLANS}
            usedFreeTrial={usedFreeTrial}
            globalPromo={globalPromo}
          />
        </div>
      )}
      {!isLoading && !plansPending && lifetimeEnabled && <LifetimePromo />}
      {isMobile ? (
        <div className="group-subscriptions-ad wrapper">
          <section>
            <h2 className="title-division">Group Learning</h2>
            <p>
              Looking for{' '}
              <a href={GROUP_SUBSCRIPTION_INFO_URL}>
                Group Learning solutions
              </a>
              ?
            </p>
            <p>
              We offer subscription discounts for educational institutions and
              organizations with five (5) or more students, including
              customizable student access to our online library to fit a variety
              of curriculum and program goals.
              <br />
              <br />
              <a href={GROUP_SUBSCRIPTION_INFO_URL}>
                {' '}
                Check out our Group Learning plans{' '}
              </a>
            </p>
          </section>
        </div>
      ) : null}
      <div className="testimonial-holder">
        <div className="overlay" />
        <div className="smaller-wrapper">
          <TestimonialVideos />
        </div>
      </div>
      <section className="faq smaller-wrapper">
        <h2 className="title-division">Frequently Asked Questions</h2>
        {FAQ_ITEMS.map((item) => (
          <Collapsible
            key={item.question}
            transitionTime={300}
            easing="ease-in-out"
            trigger={item.question}
          >
            {item.answer.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </Collapsible>
        ))}
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ_ITEMS.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer.join(' ')
              }
            }))
          }}
        />
      </section>
    </main>
  )
}

SubscriptionPlans.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  usedFreeTrial: PropTypes.bool.isRequired,
  lifetimeEnabled: PropTypes.bool,
  lifetimeTestEligible: PropTypes.bool,
  setLifetimeTestVariant: PropTypes.func,
  globalPromo: PropTypes.shape({
    codename: PropTypes.string,
    discount: PropTypes.number,
    discountType: PropTypes.string,
    plans: PropTypes.shape({
      id: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        price: PropTypes.number,
        discountAmount: PropTypes.number,
        priceDiscount: PropTypes.number
      })
    })
  })
}

export default connect(
  subscriptionPlanSelectors,
  dispatchSelector
)(SubscriptionPlans)
